import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from 'shadcn/components/ui/Button';
import { fromUrl, pushToUrl, removeEmptyFilters, toApi } from 'shared';
import { useIsFeatureOn } from 'shared/hooks/featureFlags/useIsFeatureOn';

import { PurchaseOrderSortConfig, PurchaseOrderTransformConfig } from './PurchaseOrderFilterConfig';
import { PurchaseOrderFilters } from './PurchaseOrderFilters';
import { PurchaseOrdersTable } from './PurchaseOrdersTable';
import { ReasonCodeDialog } from './ReasonCodeDialog';
import { usePurchaseOrderActions } from './hooks/usePurchaseOrderActions';
import { usePurchaseOrdersV2Search } from './queries/PurchaseOrderQueries';

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const BULK_CREATE_PURCHASE_ORDERS_FEATURE_FLAG = 'PROCUREMENT_BULK_PO_V2';
const PURCHASE_ORDER_TYPE = 'PURCHASE_ORDER';

export function ViewPurchaseOrdersV2() {
    const history = useHistory();
    const location = useLocation();
    const isBulkPurchaseOrdersOn = useIsFeatureOn(BULK_CREATE_PURCHASE_ORDERS_FEATURE_FLAG);

    // Parse initial state from URL using shared transformer (URL → UI)
    // This includes filters AND pageSize (but NOT cursor - that's session-only state)
    const [initialState] = useState(() => {
        const parsed = fromUrl(location.search, PurchaseOrderTransformConfig);
        return {
            ...parsed,
            pageSize: parsed.pageSize ? Number(parsed.pageSize) : DEFAULT_PAGE_SIZE,
            purchaseOrderTypes: [PURCHASE_ORDER_TYPE],
        };
    });

    // Unified state: filters + pageSize (cursor is managed separately)
    const [filters, setFilters] = useState(initialState);
    const {
        cancelDialog,
        cancelState,
        canSendToVendor,
        confirmCancel,
        downloadCsv,
        downloadPdf,
        downloadShipList,
        sendToVendor,
    } = usePurchaseOrderActions();

    // Cursor state (not persisted to URL - resets on page refresh)
    const [currentCursor, setCurrentCursor] = useState(undefined);

    // For paginated mode: track cursor history for back navigation
    const [cursorHistory, setCursorHistory] = useState([]);

    // Store totalCount from first request to pass to subsequent requests
    // Using a ref to avoid re-renders and duplicate queries
    // (API optimization: avoids repeated count queries on backend)
    const storedTotalCountRef = useRef(undefined);

    // Current page size from unified state
    const pageSize = filters.pageSize || DEFAULT_PAGE_SIZE;

    // Transform filters for API using shared utility (UI → API)
    const apiFilters = useMemo(() => {
        const transformed = toApi(filters, PurchaseOrderTransformConfig);
        // Ensure pageSize is always set
        if (!transformed.pageSize) {
            transformed.pageSize = DEFAULT_PAGE_SIZE;
        }
        return transformed;
    }, [filters]);

    // Note: storedTotalCountRef is read here but not in deps - we only want to
    // include it when cursor changes (pagination), not trigger new queries when it updates
    const paginatedFilters = useMemo(
        () => ({
            ...apiFilters,
            cursor: currentCursor,
            // Pass stored totalCount to avoid repeated count queries on backend
            // Only include for subsequent pages (when cursor is set)
            ...(currentCursor !== undefined &&
                storedTotalCountRef.current !== undefined && {
                    totalCount: storedTotalCountRef.current,
                }),
        }),
        [apiFilters, currentCursor],
    );

    const paginatedQuery = usePurchaseOrdersV2Search(paginatedFilters, {
        enabled: true,
    });

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const parsed = fromUrl(location.search, PurchaseOrderTransformConfig);
        const nextState = {
            ...parsed,
            pageSize: parsed.pageSize ? Number(parsed.pageSize) : DEFAULT_PAGE_SIZE,
            purchaseOrderTypes: [PURCHASE_ORDER_TYPE],
        };
        setCursorHistory([]);
        setCurrentCursor(undefined);
        storedTotalCountRef.current = undefined;
        setFilters(nextState);
    }, [location.search]);

    // Store totalCount from paginated response (optimization for subsequent page requests)
    useEffect(() => {
        if (paginatedQuery.data?.pagination?.totalCount !== undefined) {
            storedTotalCountRef.current = paginatedQuery.data.pagination.totalCount;
        }
    }, [paginatedQuery.data?.pagination?.totalCount]);

    const { data } = paginatedQuery;
    const { isLoading } = paginatedQuery;
    const { isFetching } = paginatedQuery;
    const { error } = paginatedQuery;
    const { refetch } = paginatedQuery;

    // Flatten pages into single array of rows
    const rows = useMemo(() => data?.rows ?? [], [data]);

    // Get pagination info
    const pagination = useMemo(() => data?.pagination ?? {}, [data]);

    // Use stored total count in paginated mode to preserve display across page changes
    const totalCount = pagination.totalCount ?? storedTotalCountRef.current ?? 0;
    const deptLeadCheck = data?.deptLeadCheck ?? false;

    // ==================== Handlers ====================

    // Handle filter changes - reset pagination synchronously to avoid double-render
    const handleFilterChange = useCallback(
        (nextFilters) => {
            // Merge with current pageSize and preserve sort state
            const updatedState = {
                ...nextFilters,
                pageSize: filters.pageSize || DEFAULT_PAGE_SIZE,
                ...(filters.sortField && { sortField: filters.sortField }),
                ...(filters.sortDir && { sortDir: filters.sortDir }),
            };

            // Reset pagination state (cursor is separate state, not in filters)
            setCursorHistory([]);
            setCurrentCursor(undefined);
            storedTotalCountRef.current = undefined;

            setFilters(updatedState);
            // Sync to URL using shared transformer (UI → URL)
            // Filter changes create history entries (replace: false)
            const cleanedFilters = removeEmptyFilters(updatedState);
            pushToUrl(cleanedFilters, PurchaseOrderTransformConfig, { replace: false });
        },
        [filters.pageSize, filters.sortField, filters.sortDir],
    );

    const handleNextPage = useCallback(() => {
        if (pagination.hasMore && pagination.nextCursor) {
            // Save current cursor to history for back navigation
            setCursorHistory((prev) => [...prev, currentCursor]);
            // Navigate to next page (cursor not persisted to URL)
            setCurrentCursor(pagination.nextCursor);
        }
    }, [pagination, currentCursor]);

    // Paginated mode: go to previous page
    const handlePreviousPage = useCallback(() => {
        if (cursorHistory.length > 0) {
            const newHistory = [...cursorHistory];
            const previousCursor = newHistory.pop();
            setCursorHistory(newHistory);
            // Navigate to previous page (cursor not persisted to URL)
            setCurrentCursor(previousCursor);
        }
    }, [cursorHistory]);

    // Handle page size change
    const handlePageSizeChange = useCallback(
        (newPageSize) => {
            // Update pageSize, reset cursor (go back to first page)
            const nextState = { ...filters, pageSize: newPageSize };

            setCursorHistory([]);
            setCurrentCursor(undefined);
            storedTotalCountRef.current = undefined;
            setFilters(nextState);

            // Sync pageSize to URL (replaceState for pagination changes)
            pushToUrl(removeEmptyFilters(nextState), PurchaseOrderTransformConfig, { replace: true });
        },
        [filters],
    );

    const sorting = useMemo(() => {
        const apiField = filters.sortField;
        if (!apiField) return [];
        const columnId = Object.entries(PurchaseOrderSortConfig.sortableFields).find(
            ([, cfg]) => cfg.apiField === apiField,
        )?.[0];
        if (!columnId) return [];
        return [{ id: columnId, desc: filters.sortDir === 'desc' }];
    }, [filters.sortField, filters.sortDir]);

    const handleSortingChange = useCallback(
        (updater) => {
            const nextSorting = typeof updater === 'function' ? updater(sorting) : updater;
            const sortEntry = nextSorting?.[0];
            const sortField = sortEntry ? PurchaseOrderSortConfig.sortableFields[sortEntry.id]?.apiField : undefined;
            let sortDir;
            if (sortEntry == null) {
                sortDir = undefined;
            } else {
                sortDir = sortEntry.desc ? 'desc' : 'asc';
            }

            const { sortDir: _sd, sortField: _sf, ...restFilters } = filters;
            const nextState = sortField ? { ...filters, sortField, sortDir } : restFilters;
            setCursorHistory([]);
            setCurrentCursor(undefined);
            storedTotalCountRef.current = undefined;
            setFilters(nextState);
            pushToUrl(removeEmptyFilters(nextState), PurchaseOrderTransformConfig, { replace: true });
        },
        [filters, sorting],
    );

    /**
     * Navigate to the Create page, preserving the current query string
     * so filter state can be restored when navigating back.
     */
    const handleCreatePurchaseOrder = useCallback(() => {
        const currentFilters =
            (typeof window !== 'undefined' && window?.location?.search ? window.location.search : '') ?? '';

        history.push({
            pathname: '/procurement/createPurchaseOrder-v2',
            state: { purchaseOrdersFilters: currentFilters },
        });
    }, [history]);

    const handleDuplicatePurchaseOrder = useCallback(
        (purchaseOrder) => {
            const purchaseOrderId = purchaseOrder?.id;
            const currentFilters =
                (typeof window !== 'undefined' && window?.location?.search ? window.location.search : '') ?? '';

            history.push({
                pathname: '/procurement/createPurchaseOrder-v2',
                search: purchaseOrderId ? `?duplicateFrom=${encodeURIComponent(purchaseOrderId)}` : '',
                state: { purchaseOrdersFilters: currentFilters },
            });
        },
        [history],
    );

    const handleBulkCreatePurchaseOrders = useCallback(() => {
        const currentFilters =
            (typeof window !== 'undefined' && window?.location?.search ? window.location.search : '') ?? '';

        history.push({
            pathname: '/procurement/bulkCreatePurchaseOrders-v2',
            state: { purchaseOrdersFilters: currentFilters },
        });
    }, [history]);

    const handleCancelPurchaseOrder = useCallback(
        (purchaseOrder) => {
            cancelDialog.open(purchaseOrder);
        },
        [cancelDialog],
    );

    const handleDownloadCsv = useCallback(
        (purchaseOrder) => {
            downloadCsv(purchaseOrder);
        },
        [downloadCsv],
    );

    const handleDownloadPdf = useCallback(
        (purchaseOrder) => {
            downloadPdf(purchaseOrder);
        },
        [downloadPdf],
    );

    const handleDownloadShipList = useCallback(
        (purchaseOrder) => {
            downloadShipList(purchaseOrder);
        },
        [downloadShipList],
    );

    const handleSendToVendor = useCallback(
        async (purchaseOrder) => {
            const response = await sendToVendor(purchaseOrder, deptLeadCheck);
            if (!response?.status) {
                const message = response?.msg ? `Failed to send PO. ${response.msg}` : 'Failed to send PO.';
                toast.error(message);
                return;
            }
            toast.success('Sent PO to Vendor.');
            await refetch();
        },
        [deptLeadCheck, refetch, sendToVendor],
    );

    const isSendToVendorDisabled = useCallback(
        (purchaseOrder) => !canSendToVendor(purchaseOrder, deptLeadCheck),
        [canSendToVendor, deptLeadCheck],
    );

    const handleNotify = useCallback(({ level, text }) => {
        if (level === 'error') toast.error(text);
        else if (level === 'success') toast.success(text);
        else if (level === 'warning') toast.warning(text);
        else if (level === 'info') toast.info(text);
    }, []);

    const handleCloseReasonCodeDialog = useCallback(() => {
        cancelDialog.close();
    }, [cancelDialog]);

    const handleConfirmCancelPurchaseOrder = useCallback(
        async (reasonCode) => {
            try {
                const response = await confirmCancel(reasonCode);
                if (!response?.status) {
                    const message = response?.msg ? `Unable to cancel PO. ${response.msg}` : 'Unable to cancel PO.';
                    toast.error(message);
                    return;
                }
                cancelDialog.close();
                await refetch();
            } catch (err) {
                toast.error(`Unable to cancel PO. ${err?.message ?? ''}`.trim());
            }
        },
        [cancelDialog, confirmCancel, refetch],
    );

    // Handle error notification
    useEffect(() => {
        if (error) {
            const messageParts = ['Unable to load purchase orders.', error.message];
            toast.error(messageParts.filter(Boolean).join(' '));
        }
    }, [error]);

    // Calculate current page number for paginated mode
    const currentPage = cursorHistory.length + 1;
    const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : undefined;

    // Extract just the filter values (without pageSize or sort) for the filter UI
    const filterValuesForUI = useMemo(() => {
        const { pageSize: _ps, sortDir: _sd, sortField: _sf, ...filterOnly } = filters;
        return filterOnly;
    }, [filters]);

    return (
        <div className="flex min-h-screen flex-col">
            <div className="px-4 pt-4">
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-semibold">View Purchase Orders</h1>
                    <div>
                        {isBulkPurchaseOrdersOn.data && (
                            <Button size="sm" className="mr-2" onClick={handleBulkCreatePurchaseOrders}>
                                Bulk Create Purchase Orders
                            </Button>
                        )}
                        <Button size="sm" onClick={handleCreatePurchaseOrder}>
                            Create Purchase Order
                        </Button>
                    </div>
                </div>
                <div className="mt-4">
                    <PurchaseOrderFilters initialValues={filterValuesForUI} onChange={handleFilterChange} />
                </div>
            </div>
            <div className="flex flex-1 flex-col">
                <PurchaseOrdersTable
                    data={rows}
                    totalCount={totalCount}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    hasNextPage={pagination.hasMore}
                    // Paginated props
                    hasPrevious={cursorHistory.length > 0}
                    onPrevious={handlePreviousPage}
                    onNext={handleNextPage}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    // Page size props
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    sorting={sorting}
                    onSortingChange={handleSortingChange}
                    onCancelPurchaseOrder={handleCancelPurchaseOrder}
                    onDuplicatePurchaseOrder={handleDuplicatePurchaseOrder}
                    onDownloadCsv={handleDownloadCsv}
                    onDownloadPdf={handleDownloadPdf}
                    onDownloadShipList={handleDownloadShipList}
                    onSendToVendor={handleSendToVendor}
                    isSendToVendorDisabled={isSendToVendorDisabled}
                    onNotify={handleNotify}
                />
            </div>
            <ReasonCodeDialog
                open={cancelDialog.isOpen}
                title="Select A Reason Code and Confirm"
                onCancel={handleCloseReasonCodeDialog}
                onConfirm={handleConfirmCancelPurchaseOrder}
                isSubmitting={cancelState.isSubmitting}
            />
        </div>
    );
}
